import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import { IoCheckmarkCircle } from 'react-icons/io5';

import { Stack, Text, Title } from '@mantine/core';

import { UserReferrer, UserType } from 'models';
import { CompanyHelpers } from 'helpers/CompanyHelpers';

import { useLocalUserInfo } from 'hooks/use-local-user-info';
import { useWorkspaceConfig } from 'hooks/use-workspace-config';

const LoginTitle = ({
    overrideTitle,
    message: incomingMessage,
}: {
    overrideTitle?: string | React.ReactNode;
    message?: React.ReactNode;
}) => {
    const router = useRouter();

    const queryTitle = router.query.title as string | undefined;
    const redirect = router.query.redirect;
    const { type, referrer, setReferrer, setType } = useLocalUserInfo();
    const { logoUrl, name: workspaceName } = useWorkspaceConfig();

    const isClaimProfile =
        (redirect?.includes('/profiles/') && redirect.includes('/request-access')) ||
        (router.pathname.includes('/profiles/') && router.pathname.includes('/request-access'));

    const isInvitation = !!router.query.invitation;

    useEffect(() => {
        if (isClaimProfile) {
            setReferrer(UserReferrer.REPLUS);
            setType(UserType.MANUFACTURER);
        }
    }, [isClaimProfile]);

    let content = (
        <Stack align="center" gap="lg">
            {queryTitle ? (
                <>{queryTitle}</>
            ) : logoUrl ? (
                <>
                    Get Access To{' '}
                    <img src={logoUrl} alt={workspaceName || 'Logo'} style={{ maxWidth: '60%', maxHeight: 80 }} />
                </>
            ) : (
                <>Get Instant Access</>
            )}
        </Stack>
    );

    let message = incomingMessage;

    if (isInvitation) {
        content = (
            <Stack align="center" gap={8}>
                <IoCheckmarkCircle size={60} color="var(--mantine-color-green-6)" />
                Accept Invitation and Create Account
            </Stack>
        );
    }

    if (isClaimProfile) {
        content = <>Claim Your Profile</>;
    }

    if (referrer === UserReferrer.REPLUS && type === UserType.MANUFACTURER) {
        content = <>Create a Profile</>;
    }

    if (redirect?.includes(CompanyHelpers.urls.create())) {
        content = <>Ready to join as a PRO?</>;
        message = (
            <Text span c="black" fw={500} fz="lg">
                <strong>You need to create a user account first.</strong> <br />
                <br /> To speed up verification of your account, consider signing up using your professional email
                address.
            </Text>
        );
    }

    if (redirect === '/components') {
        content = <>Get Access to the Product Catalog</>;
    }

    if (redirect === '/designs') {
        content = <>Get Access to the Design Marketplace</>;
    }

    if (typeof redirect === 'string' && redirect.startsWith('/projects')) {
        content = (
            <>
                Get Access to the <br />
                DC Microgrid Design Software
            </>
        );
    }

    return (
        <Stack my="xl" gap="xl">
            <Title order={1} fz={24} fw={800} c="black" ta="center">
                {overrideTitle || content}
            </Title>
            <DefaultMessage message={message} />
        </Stack>
    );
};

const DefaultMessage = ({
    message = (
        <>
            New or existing user?
            <br />
            We will send you a login link, no credit card needed.
        </>
    ),
}: {
    message?: React.ReactNode;
}) => {
    return (
        <Text ta="center" c="dimmed" fw={500}>
            {message}
        </Text>
    );
};

export { LoginTitle };
